import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, WifiOff } from 'lucide-react';

interface LiterallyLoaderProps {
  onComplete?: () => void;
  hasError?: boolean;
  onRetry?: () => void;
  errorMsg?: string;
  statusText?: string;
}

const LITERARY_STATUSES = [
  "Curating stories, verse & imagination...",
  "Connecting authors and storytellers...",
  "Preparing your personal library...",
  "Distraction-free publishing & reading...",
];

export const LiterallyLoader: React.FC<LiterallyLoaderProps> = ({ 
  hasError = false, 
  onRetry, 
  errorMsg = "Unable to connect to Literally. Please check your internet connection.",
  statusText
}) => {
  const [statusIndex, setStatusIndex] = useState(0);

  useEffect(() => {
    if (hasError || statusText) return;
    const interval = setInterval(() => {
      setStatusIndex((prev) => (prev + 1) % LITERARY_STATUSES.length);
    }, 2400);
    return () => clearInterval(interval);
  }, [hasError, statusText]);

  return (
    <div className="fixed inset-0 bg-[#020617] flex flex-col items-center justify-center min-h-screen px-6 z-[999999] overflow-hidden select-none">
      {/* Ambient Radial Glows matching HTML splash */}
      <div className="absolute top-[35%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[360px] h-[360px] bg-[#5F4BFF]/20 rounded-full blur-[90px] pointer-events-none" />
      <div className="absolute top-[55%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-purple-600/15 rounded-full blur-[100px] pointer-events-none" />

      {/* Main Centered Content */}
      <div className="relative z-10 flex flex-col items-center max-w-sm w-full text-center">
        
        {/* Animated Brand Logo Squircle */}
        <motion.div
          animate={hasError ? {
            y: [0, -3, 0],
            scale: [1, 1.02, 1]
          } : {
            scale: [1, 1.05, 1],
            boxShadow: [
              "0 0 50px -6px rgba(95, 75, 255, 0.55)",
              "0 0 70px -2px rgba(95, 75, 255, 0.8)",
              "0 0 50px -6px rgba(95, 75, 255, 0.55)"
            ]
          }}
          transition={hasError ? {
            duration: 2.5,
            repeat: Infinity,
            ease: "easeInOut"
          } : {
            duration: 2.2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="w-20 h-20 rounded-[24px] bg-[#5F4BFF] flex items-center justify-center relative overflow-hidden shadow-[0_0_50px_-6px_rgba(95,75,255,0.6)]"
        >
          {hasError ? (
            <div className="w-full h-full bg-rose-600 flex items-center justify-center text-white">
              <WifiOff size={32} />
            </div>
          ) : (
            <svg
              width="48"
              height="48"
              viewBox="0 0 512 512"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="drop-shadow-[0_2px_10px_rgba(0,0,0,0.35)]"
            >
              <path d="M222 125 H297 L253 310 H350 V385 H160 Z" fill="#FFFFFF" />
            </svg>
          )}
        </motion.div>

        {/* Wordmark and Tagline */}
        <div className="mt-6 flex flex-col items-center">
          <motion.h1
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-serif font-bold italic text-4xl sm:text-5xl text-white tracking-tight leading-none drop-shadow-[0_2px_14px_rgba(0,0,0,0.7)]"
          >
            Literally.
          </motion.h1>

          <span className="mt-2.5 text-[10px] font-bold tracking-[0.32em] uppercase text-slate-400">
            Social Publishing Platform
          </span>
        </div>

        {/* Dynamic State: Progress Bar & Status OR Error Prompt */}
        <AnimatePresence mode="wait">
          {!hasError ? (
            <motion.div
              key="loader-progress"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="mt-7 flex flex-col items-center w-full"
            >
              {/* Glowing Animated Progress Bar */}
              <div className="w-36 h-[3px] bg-white/10 rounded-full overflow-hidden relative">
                <motion.div
                  className="absolute top-0 bottom-0 w-[55%] bg-gradient-to-r from-[#5F4BFF] via-indigo-400 to-purple-400 rounded-full shadow-[0_0_12px_rgba(95,75,255,0.9)]"
                  animate={{
                    x: ['-100%', '40%', '130%'],
                  }}
                  transition={{
                    duration: 1.6,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
              </div>

              {/* Status Indicator */}
              <div className="h-6 mt-3.5 flex items-center justify-center">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={statusText || statusIndex}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.25 }}
                    className="text-xs text-slate-400 font-medium"
                  >
                    {statusText || LITERARY_STATUSES[statusIndex]}
                  </motion.p>
                </AnimatePresence>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="loader-error"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              className="mt-6 flex flex-col items-center space-y-3 px-4 max-w-xs"
            >
              <div className="flex items-center gap-1.5 text-rose-400 font-bold text-xs uppercase tracking-wider">
                <WifiOff size={15} />
                <span>Connection Paused</span>
              </div>

              <p className="text-xs text-slate-400 leading-relaxed">
                {errorMsg}
              </p>

              {onRetry && (
                <button
                  onClick={onRetry}
                  className="mt-2 flex items-center justify-center gap-2 px-6 py-2.5 bg-[#5F4BFF] hover:bg-indigo-600 active:scale-95 text-white rounded-full text-xs font-bold transition-all shadow-lg shadow-[#5F4BFF]/30"
                >
                  <RefreshCw size={14} className="animate-spin" />
                  Try Again
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LiterallyLoader;
